import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_BASE_URL, API_KEY, getApiHeaders } from '../config/api';
import {
  inspectProfileFrameFile,
  isProfileFrameType,
  orderProfileFrameMediaAssets,
  validateProfileFrameProduct,
} from '../utils/profileFrameRules';
import {
  inspectAnimatedBackgroundFile,
  isAnimatedBackgroundType,
  orderAnimatedBackgroundMediaAssets,
  validateAnimatedBackgroundProduct,
} from '../utils/profileBackgroundRules';

type WalletShopMediaAsset = {
  Type: string;
  Url: string;
  ThumbnailUrl?: string;
  MimeType?: string;
  DurationSeconds?: number;
  Width?: number;
  Height?: number;
};

type WalletShopProduct = {
  ProductKey: string;
  Name: string;
  Description?: string;
  CategoryKey: FetchCategoryKey;
  Type: string;
  Tags?: string[];
  MediaAssets?: WalletShopMediaAsset[];
  AvailabilityType: string;
  AvailabilityStartAt?: string;
  AvailabilityEndAt?: string;
  QuantityAvailable?: number;
  QuantitySold?: number;
  PerUserLimit?: number;
  MaxPurchasePerOrder?: number;
  CurrencyType?: 'coin' | 'token';
  PriceCoins: number;
  PriceTokens?: number;
  DiscountPriceCoins?: number;
  DiscountStartAt?: string;
  DiscountEndAt?: string;
  IsFeatured?: boolean;
  BadgeText?: string;
  DisplayOrder?: number;
  Metadata?: Record<string, any>;
  ThemePreference?: 'adaptive' | 'light' | 'dark';
  IsActive?: boolean;
  Environment?: 'dev' | 'prod';
};

type UploadResult = {
  ProductKey: string;
  AssetType: string;
  ContentType: string;
  AssetUrl: string;
  UploadUrl: string;
  Bucket: string;
  Key: string;
};

type PendingMediaAsset = {
  Id: string;
  Type: string;
  File?: File;
  PreviewUrl?: string;
  UrlValue?: string;
  Width?: number;
  Height?: number;
};

const walletApiBase = `${API_BASE_URL.replace(/\/$/, '')}/wallet/t`;
type WalletShopCategoryKey = 'PROFILE' | 'GIFT' | 'LIVE';
type FetchCategoryKey = WalletShopCategoryKey | 'STICKER' | 'EVENT_GIFT';

const productTypes = [
  { value: 'digital_gift', label: 'Digital Gift' },
  { value: 'profile_frame', label: 'Profile Frame' },
  { value: 'profile_frame_animated', label: 'Animated Profile Frame' },
  { value: 'background', label: 'Background' },
  { value: 'background_animated', label: 'Animated Background' },
  { value: 'chat_themes', label: 'Chat Themes' },
  { value: 'boost', label: 'Boost' },
  { value: 'utility', label: 'Utility' },
  { value: 'profile_badge', label: 'Profile Badge' },
  { value: 'profile_animation', label: 'Profile Animation' },
  { value: 'entry_animation', label: 'Entry Animation' },
  { value: 'sticker', label: 'Sticker' },
  { value: 'event_gift', label: 'Event Gift' },
];
const allowedProductTypeByCategory: Record<FetchCategoryKey, string[]> = {
  GIFT: ['digital_gift'],
  PROFILE: ['profile_frame', 'profile_frame_animated', 'background', 'background_animated', 'chat_themes', 'boost', 'utility', 'profile_badge', 'profile_animation'],
  LIVE: ['entry_animation'],
  STICKER: ['sticker'],
  EVENT_GIFT: ['event_gift'],
};

const resolveProductTypeForCategory = (category: FetchCategoryKey, productType?: string) => {
  const allowedTypes = allowedProductTypeByCategory[category] || [];
  if (productType && allowedTypes.includes(productType)) return productType;
  return allowedTypes[0] || productTypes[0].value;
};

const availabilityTypes = [
  { value: 'unlimited', label: 'Unlimited' },
  { value: 'limited', label: 'Limited' },
];

const mediaTypes = [
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'lottie', label: 'Lottie (JSON)' },
  { value: 'animation', label: 'Animation (JSON)' },
  { value: 'preview', label: 'Preview (JSON)' },
  { value: 'audio', label: 'Audio (Sound Effect)' },
  { value: 'webm', label: 'Animation (WebM Video — Android)' },
  { value: 'ios_alpha', label: 'Animation (MOV/MP4 HEVC Alpha — iOS)' },
  { value: 'live_webm', label: 'Live Profile Animation (WebM — Android)' },
  { value: 'live_ios_alpha', label: 'Live Profile Animation (MOV/MP4 HEVC Alpha — iOS)' },
  { value: 'thumbnail', label: 'Thumbnail' },
  { value: 'light_mode', label: 'Light Mode' },
  { value: 'dark_mode', label: 'Dark Mode' },
  { value: 'theme_light_mode', label: 'Theme Light Mode' },
  { value: 'theme_dark_mode', label: 'Theme Dark Mode' },
  { value: 'thumbnail_light_mode', label: 'Thumbnail Light Mode' },
  { value: 'thumbnail_dark_mode', label: 'Thumbnail Dark Mode' },
  { value: 'bubble_light_mode', label: 'Bubble Light Mode' },
  { value: 'bubble_dark_mode', label: 'Bubble Dark Mode' },
];

type ProductMediaRule = {
  allowedTypes: string[];
  requiredTypes: string[];
  helperText: string;
};

const mediaTypeLabels = new Map(mediaTypes.map((mediaType) => [mediaType.value, mediaType.label]));
const allMediaTypeValues = mediaTypes.map((mediaType) => mediaType.value);

const productMediaRules: Record<string, ProductMediaRule> = {
  digital_gift: {
    allowedTypes: ['lottie', 'animation', 'preview', 'audio', 'webm', 'ios_alpha', 'thumbnail'],
    requiredTypes: [],
    helperText: 'Digital gifts use a lottie or animation JSON file. Add a WebM video (Android) and a MOV/MP4 HEVC alpha video (iOS) for high-fidelity livestream overlay, a preview JSON for the grid thumbnail, an audio file for the sound effect, and an optional static thumbnail.',
  },
  chat_themes: {
    allowedTypes: [
      'theme_light_mode',
      'theme_dark_mode',
      'thumbnail_light_mode',
      'thumbnail_dark_mode',
      'bubble_light_mode',
      'bubble_dark_mode',
    ],
    requiredTypes: [],
    helperText: 'Chat themes support light and dark theme images, thumbnails, and bubble colors according to theme preference.',
  },
  background: {
    allowedTypes: ['image', 'light_mode', 'dark_mode'],
    requiredTypes: [],
    helperText: 'Backgrounds support image, light_mode, and dark_mode assets according to theme preference.',
  },
  profile_frame: {
    allowedTypes: ['image', 'webm', 'ios_alpha'],
    requiredTypes: ['image'],
    helperText:
      'Static profile frames require one image (max 1 MB, 1024×1024). Android (.webm) and iOS (.mov) files are optional.',
  },
  background_animated: {
    allowedTypes: ['webm', 'ios_alpha', 'thumbnail'],
    requiredTypes: ['webm', 'ios_alpha', 'thumbnail'],
    helperText:
      'Animated backgrounds require all three: Android (.webm, max 15 MB), iOS (.mov, max 15 MB) and a WebP thumbnail (max 1 MB) — matching dimensions and timing, no unwanted black/opaque background, max 2160 px per side. Old app versions show the thumbnail.',
  },
  profile_frame_animated: {
    allowedTypes: ['webm', 'ios_alpha', 'thumbnail'],
    requiredTypes: ['webm', 'ios_alpha', 'thumbnail'],
    helperText:
      'Animated profile frames require Android (.webm, alpha) + iOS (.mov, alpha) (max 15 MB each) and a WebP thumbnail (max 1 MB) — all the same aspect ratio (e.g. all square), max 1024×1024. Old app versions show the thumbnail.',
  },
  entry_animation: {
    allowedTypes: ['ios_alpha', 'webm', 'thumbnail'],
    requiredTypes: ['ios_alpha', 'webm'],
    helperText:
      'Live entry (join) animations require both platform videos: iOS (.mov, alpha channel) and Android (.webm). Thumbnail is optional.',
  },
  profile_animation: {
    allowedTypes: ['ios_alpha', 'webm', 'thumbnail', 'live_ios_alpha', 'live_webm'],
    requiredTypes: ['ios_alpha', 'webm'],
    helperText:
      'Profile animations require both platform videos: iOS (.mov, alpha channel) and Android (.webm). Thumbnail is optional. ' +
      'Live Profile Animation (optional): separate iOS/Android videos shown only on the Live quick profile; when empty the app uses the normal animation.',
  },
};

const defaultProductMediaRule: ProductMediaRule = {
  allowedTypes: allMediaTypeValues,
  requiredTypes: [],
  helperText: 'Choose media slots based on the product content.',
};

const normalizeDateInput = (value?: string) => (value ? new Date(value).toISOString().slice(0, 19) : '');
const directValueMediaTypes = new Set(['bubble_light_mode', 'bubble_dark_mode']);
const jsonLikeMediaTypes = new Set(['lottie', 'animation', 'preview']);
const isLikelyHttpUrl = (value: string) => /^https?:\/\//i.test(value.trim());
const isHexColor = (value: string) => /^#[0-9a-f]{3,8}$/i.test(value.trim());
const isImageFile = (file?: File) => !!file?.type?.startsWith('image/');
const isImageLikeAssetType = (type: string) =>
  ['image', 'light_mode', 'dark_mode', 'theme_light_mode', 'theme_dark_mode', 'thumbnail_light_mode', 'thumbnail_dark_mode'].includes(
    type
  );
const getMediaTypeLabel = (type: string) => mediaTypeLabels.get(type) || type;
const getProductMediaRule = (type: string) => productMediaRules[type] || defaultProductMediaRule;

// Animated backgrounds share the Background slot, so the app needs the same light/dark hint for them.
const isThemePreferenceSupported = (type: string) =>
  type === 'chat_themes' || type === 'background' || isAnimatedBackgroundType(type);

const validateMediaAssetsForType = (
  productType: string,
  mediaAssets: WalletShopMediaAsset[],
  themePreference: 'adaptive' | 'light' | 'dark' = 'adaptive'
) => {
  const rule = getProductMediaRule(productType);
  const allowedTypes = new Set(rule.allowedTypes);
  const seenTypes = new Set<string>();

  for (const asset of mediaAssets) {
    if (!allowedTypes.has(asset.Type)) {
      throw new Error(`Media slot "${asset.Type}" is not allowed for ${productType}.`);
    }
    if (seenTypes.has(asset.Type)) {
      throw new Error(`Media slot "${asset.Type}" can only be added once.`);
    }
    if (directValueMediaTypes.has(asset.Type) && !isHexColor(asset.Url)) {
      throw new Error(`Media slot "${asset.Type}" must use a hex color like #F0DBE0.`);
    }
    seenTypes.add(asset.Type);
  }

  if (productType === 'chat_themes') {
    if (themePreference === 'light') {
      if (!seenTypes.has('theme_light_mode')) {
        throw new Error('Light chat theme requires a "Theme Light Mode" asset (theme_light_mode).');
      }
    } else if (themePreference === 'dark') {
      if (!seenTypes.has('theme_dark_mode')) {
        throw new Error('Dark chat theme requires a "Theme Dark Mode" asset (theme_dark_mode).');
      }
    } else {
      if (!seenTypes.has('theme_light_mode') && !seenTypes.has('theme_dark_mode')) {
        throw new Error('Adaptive chat theme requires at least one theme asset (theme_light_mode or theme_dark_mode).');
      }
    }
    return;
  }

  if (productType === 'background') {
    if (themePreference === 'light') {
      if (!seenTypes.has('light_mode') && !seenTypes.has('image')) {
        throw new Error('Light background requires a "Light Mode" (light_mode) or "Image" (image) asset.');
      }
    } else if (themePreference === 'dark') {
      if (!seenTypes.has('dark_mode') && !seenTypes.has('image')) {
        throw new Error('Dark background requires a "Dark Mode" (dark_mode) or "Image" (image) asset.');
      }
    } else {
      if (!seenTypes.has('light_mode') && !seenTypes.has('dark_mode') && !seenTypes.has('image')) {
        throw new Error('Adaptive background requires at least one background asset (light_mode, dark_mode, or image).');
      }
    }
    return;
  }

  const missingRequiredTypes = rule.requiredTypes.filter((requiredType) => !seenTypes.has(requiredType));
  if (missingRequiredTypes.length > 0) {
    throw new Error(`Missing required media slots: ${missingRequiredTypes.map(getMediaTypeLabel).join(', ')}`);
  }
};

const UpdateWalletShopProduct: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productKeyParam = (searchParams.get('productKey') || '').trim().toUpperCase();
  const rawCategoryParam = searchParams.get('category');
  // fetchCategoryParam is what the list page actually sends (STICKER/EVENT_GIFT/LIVE included)
  // and must be used verbatim when querying the product, otherwise stickers/event gifts/entry
  // animations collapse into PROFILE and the lookup never finds the existing product.
  const fetchCategoryParam: FetchCategoryKey =
    rawCategoryParam === 'STICKER' || rawCategoryParam === 'EVENT_GIFT' || rawCategoryParam === 'GIFT' || rawCategoryParam === 'LIVE'
      ? rawCategoryParam
      : 'PROFILE';
  const typeParam = searchParams.get('type') || '';
  const environmentParam = (searchParams.get('env') || '').trim() as 'dev' | 'prod' | '';
  const initialProductType = resolveProductTypeForCategory(fetchCategoryParam, typeParam || undefined);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draftFile, setDraftFile] = useState<File | null>(null);
  const [assetDraft, setAssetDraft] = useState({
    Type: mediaTypes[0].value,
    UrlValue: '',
  });
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [originalPriceCoins, setOriginalPriceCoins] = useState<number | null>(null);
  const [originalPriceTokens, setOriginalPriceTokens] = useState<number | null>(null);
  const [pendingMediaAssets, setPendingMediaAssets] = useState<PendingMediaAsset[]>([]);
  const pendingAssetsRef = useRef<PendingMediaAsset[]>([]);
  const [draftPreviewUrl, setDraftPreviewUrl] = useState('');
  const previousProductTypeRef = useRef(initialProductType);

  const [mediaAssets, setMediaAssets] = useState<WalletShopMediaAsset[]>([]);
  const [formState, setFormState] = useState({
    ProductKey: productKeyParam,
    Name: '',
    Description: '',
    CategoryKey: fetchCategoryParam as FetchCategoryKey,
    Type: initialProductType,
    ThemePreference: 'adaptive' as 'adaptive' | 'light' | 'dark',
    Tags: '',
    AvailabilityType: availabilityTypes[0].value,
    AvailabilityStartAt: '',
    AvailabilityEndAt: '',
    QuantityAvailable: '',
    QuantitySold: '',
    PerUserLimit: '',
    MaxPurchasePerOrder: '',
    CurrencyType: (fetchCategoryParam === 'LIVE' ? 'token' : 'coin') as 'coin' | 'token',
    PriceCoins: '',
    PriceTokens: '',
    DiscountPriceCoins: '',
    DiscountStartAt: '',
    DiscountEndAt: '',
    IsFeatured: false,
    BadgeText: '',
    DisplayOrder: '',
    Metadata: '',
    IsActive: true,
    Environment: environmentParam || 'prod',
  });
  const allowedProductTypes = useMemo(() => {
    const allowedTypes = new Set(allowedProductTypeByCategory[formState.CategoryKey] || []);
    return productTypes.filter((productType) => allowedTypes.has(productType.value));
  }, [formState.CategoryKey]);
  const allowedProductTypeValues = useMemo(() => new Set(allowedProductTypes.map((productType) => productType.value)), [allowedProductTypes]);

  const mediaRule = useMemo(() => getProductMediaRule(formState.Type), [formState.Type]);
  const usedMediaTypeSet = useMemo(
    () => new Set([...mediaAssets.map((asset) => asset.Type), ...pendingMediaAssets.map((asset) => asset.Type)]),
    [mediaAssets, pendingMediaAssets]
  );
  const hasAvailableMediaSlots = useMemo(
    () => mediaRule.allowedTypes.some((type) => !usedMediaTypeSet.has(type)),
    [mediaRule.allowedTypes, usedMediaTypeSet]
  );
  const dynamicRequiredTypes = useMemo(() => {
    if (formState.Type === 'chat_themes') {
      if (formState.ThemePreference === 'light') return ['theme_light_mode'];
      if (formState.ThemePreference === 'dark') return ['theme_dark_mode'];
      return ['theme_light_mode', 'theme_dark_mode'];
    }
    if (formState.Type === 'background') {
      // Theme Preference only tells the app which mode to render in — it doesn't need
      // a matching asset. Any one background asset (image, light_mode or dark_mode)
      // is enough, same as validateMediaAssetsForType.
      const hasBackgroundAsset = mediaAssets.some((asset) =>
        ['image', 'light_mode', 'dark_mode'].includes(asset.Type)
      );
      return hasBackgroundAsset ? [] : ['image'];
    }
    return mediaRule.requiredTypes;
  }, [formState.Type, formState.ThemePreference, mediaRule.requiredTypes, mediaAssets]);

  const missingRequiredMediaTypes = useMemo(
    () =>
      dynamicRequiredTypes.filter(
        (requiredType) => !mediaAssets.some((asset) => asset.Type === requiredType)
      ),
    [mediaAssets, dynamicRequiredTypes]
  );

  const resolveUserId = () => (localStorage.getItem('wallet_assets_user_id') || '').trim();

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const buildNumber = (value: string, label: string) => {
    if (!value.trim()) return undefined;
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      throw new Error(`${label} must be a valid number.`);
    }
    return parsed;
  };

  const buildDate = (value: string) => {
    if (!value.trim()) return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      throw new Error('Invalid date format.');
    }
    return date.toISOString();
  };

  const parsedTags = useMemo(() => {
    return formState.Tags.split(',').map((tag) => tag.trim()).filter(Boolean);
  }, [formState.Tags]);

  useEffect(() => {
    if (allowedProductTypeValues.has(formState.Type)) return;
    const resolvedType = resolveProductTypeForCategory(formState.CategoryKey, formState.Type);
    if (!resolvedType || resolvedType === formState.Type) return;
    setFormState((prev) => ({ ...prev, Type: resolvedType }));
  }, [allowedProductTypeValues, formState.CategoryKey, formState.Type]);

  useEffect(() => {
    pendingAssetsRef.current = pendingMediaAssets;
  }, [pendingMediaAssets]);

  useEffect(() => {
    if (!isImageFile(draftFile || undefined)) {
      setDraftPreviewUrl('');
      return;
    }

    const localPreviewUrl = URL.createObjectURL(draftFile as File);
    setDraftPreviewUrl(localPreviewUrl);

    return () => {
      URL.revokeObjectURL(localPreviewUrl);
    };
  }, [draftFile]);

  useEffect(
    () => () => {
      pendingAssetsRef.current.forEach((pending) => {
        if (pending.PreviewUrl) {
          URL.revokeObjectURL(pending.PreviewUrl);
        }
      });
    },
    []
  );

  useEffect(() => {
    const previousType = previousProductTypeRef.current;
    if (previousType === formState.Type) return;
    previousProductTypeRef.current = formState.Type;

    const allowedTypes = new Set(mediaRule.allowedTypes);

    setMediaAssets((prev) => {
      const next = prev.filter((asset) => allowedTypes.has(asset.Type));
      if (next.length !== prev.length) {
        toast.info(`${prev.length - next.length} attached asset(s) removed due to product type change.`);
      }
      return next;
    });

    setPendingMediaAssets((prev) => {
      const next = prev.filter((asset) => allowedTypes.has(asset.Type));
      const removedAssets = prev.filter((asset) => !allowedTypes.has(asset.Type));
      removedAssets.forEach((asset) => {
        if (asset.PreviewUrl) {
          URL.revokeObjectURL(asset.PreviewUrl);
        }
      });
      if (removedAssets.length > 0) {
        toast.info(`${removedAssets.length} pending asset(s) removed due to product type change.`);
      }
      return next;
    });

    setDraftFile(null);
    setAssetDraft((prev) => ({
      ...prev,
      Type: mediaRule.allowedTypes[0] || mediaTypes[0].value,
      UrlValue: '',
    }));
  }, [formState.Type, mediaRule.allowedTypes]);

  useEffect(() => {
    if (!mediaRule.allowedTypes.length) return;

    const firstAvailableType = mediaRule.allowedTypes.find((type) => !usedMediaTypeSet.has(type));
    const isCurrentTypeAllowed = mediaRule.allowedTypes.includes(assetDraft.Type);
    const shouldMoveToFirstAvailable = firstAvailableType && usedMediaTypeSet.has(assetDraft.Type);
    const nextType =
      !isCurrentTypeAllowed || shouldMoveToFirstAvailable ? firstAvailableType || mediaRule.allowedTypes[0] : assetDraft.Type;

    if (nextType !== assetDraft.Type) {
      setAssetDraft((prev) => ({
        ...prev,
        Type: nextType,
      }));
    }
  }, [assetDraft.Type, mediaRule.allowedTypes, usedMediaTypeSet]);

  const buildPayload = () => {
    if (!formState.ProductKey.trim()) throw new Error('ProductKey is required.');
    if (!formState.Name.trim()) throw new Error('Name is required.');
    if (!formState.CategoryKey.trim()) throw new Error('CategoryKey is required.');
    const isTokenGift = formState.CurrencyType === 'token';
    if (isTokenGift) {
      if (!formState.PriceTokens.trim()) throw new Error('PriceTokens is required for token gifts.');
    } else {
      if (!formState.PriceCoins.trim()) throw new Error('PriceCoins is required.');
    }
    const allowedProductTypesForCategory = allowedProductTypeByCategory[formState.CategoryKey] || [];
    if (!allowedProductTypesForCategory.includes(formState.Type)) {
      throw new Error(`Type "${formState.Type}" is not allowed for category "${formState.CategoryKey}".`);
    }

    const payload: Record<string, any> = {
      ProductKey: formState.ProductKey.trim().toUpperCase(),
      Name: formState.Name.trim(),
      Description: formState.Description.trim(),
      CategoryKey: formState.CategoryKey,
      Type: formState.Type,
      ThemePreference: formState.ThemePreference || 'adaptive',
      AvailabilityType: formState.AvailabilityType,
      CurrencyType: formState.CurrencyType,
      PriceCoins: isTokenGift ? 0 : buildNumber(formState.PriceCoins, 'PriceCoins'),
      ...(isTokenGift && { PriceTokens: buildNumber(formState.PriceTokens, 'PriceTokens') }),
      // Single-currency products: a coin price clears any old token price (PriceTokens 0).
      ...(!isTokenGift &&
        (isProfileFrameType(formState.Type) || isAnimatedBackgroundType(formState.Type)) && { PriceTokens: 0 }),
      IsFeatured: formState.IsFeatured,
      BadgeText: formState.BadgeText.trim() || undefined,
      IsActive: formState.IsActive,
      Environment: formState.Environment,
    };

    if (parsedTags.length) payload.Tags = parsedTags;

    // Parsed up front: Live Quest reward metadata changes the price rule below.
    let metadata: Record<string, any> | undefined;
    if (formState.Metadata.trim()) {
      try {
        metadata = JSON.parse(formState.Metadata);
      } catch {
        throw new Error('Metadata must be valid JSON.');
      }
    }
    validateMediaAssetsForType(formState.Type, mediaAssets, formState.ThemePreference);
    validateProfileFrameProduct({
      Type: formState.Type,
      CurrencyType: formState.CurrencyType,
      PriceCoins: payload.PriceCoins,
      PriceTokens: payload.PriceTokens,
      IsActive: formState.IsActive,
      Metadata: metadata,
      MediaAssets: mediaAssets,
    });
    validateAnimatedBackgroundProduct({
      Type: formState.Type,
      CurrencyType: formState.CurrencyType,
      PriceCoins: payload.PriceCoins,
      PriceTokens: payload.PriceTokens,
      IsActive: formState.IsActive,
      Metadata: metadata,
      MediaAssets: mediaAssets,
    });
    if (mediaAssets.length) {
      payload.MediaAssets = orderAnimatedBackgroundMediaAssets(
        formState.Type,
        orderProfileFrameMediaAssets(formState.Type, mediaAssets)
      );
    }

    const quantityAvailable = buildNumber(formState.QuantityAvailable, 'QuantityAvailable');
    if (formState.AvailabilityType === 'limited' && quantityAvailable === undefined) {
      throw new Error('QuantityAvailable is required for limited availability.');
    }
    if (quantityAvailable !== undefined) payload.QuantityAvailable = quantityAvailable;

    const quantitySold = buildNumber(formState.QuantitySold, 'QuantitySold');
    if (quantitySold !== undefined) payload.QuantitySold = quantitySold;

    const perUserLimit = buildNumber(formState.PerUserLimit, 'PerUserLimit');
    if (perUserLimit !== undefined) payload.PerUserLimit = perUserLimit;

    const maxPurchase = buildNumber(formState.MaxPurchasePerOrder, 'MaxPurchasePerOrder');
    if (maxPurchase !== undefined) payload.MaxPurchasePerOrder = maxPurchase;

    const discountPrice = buildNumber(formState.DiscountPriceCoins, 'DiscountPriceCoins');
    if (discountPrice !== undefined) payload.DiscountPriceCoins = discountPrice;

    const displayOrder = buildNumber(formState.DisplayOrder, 'DisplayOrder');
    if (displayOrder !== undefined) payload.DisplayOrder = displayOrder;

    const availabilityStartAt = buildDate(formState.AvailabilityStartAt);
    if (availabilityStartAt) payload.AvailabilityStartAt = availabilityStartAt;

    const availabilityEndAt = buildDate(formState.AvailabilityEndAt);
    if (availabilityEndAt) payload.AvailabilityEndAt = availabilityEndAt;

    const discountStartAt = buildDate(formState.DiscountStartAt);
    if (discountStartAt) payload.DiscountStartAt = discountStartAt;

    const discountEndAt = buildDate(formState.DiscountEndAt);
    if (discountEndAt) payload.DiscountEndAt = discountEndAt;

    if (metadata !== undefined) payload.Metadata = metadata;

    return payload;
  };

  const upsertMediaAssets = (incomingAssets: WalletShopMediaAsset[]) => {
    if (!incomingAssets.length) return;
    setMediaAssets((prev) => {
      const next = [...prev];
      incomingAssets.forEach((incoming) => {
        const existingIndex = next.findIndex((asset) => asset.Type === incoming.Type);
        if (existingIndex >= 0) {
          next[existingIndex] = incoming;
          return;
        }
        next.push(incoming);
      });
      return next;
    });
  };

  const fetchProduct = useCallback(async () => {
    const userId = resolveUserId();
    if (!userId) {
      toast.error('UserID is required to fetch products.');
      setLoading(false);
      return;
    }
    if (!productKeyParam) {
      toast.info('No productKey in URL. Fill details to create a product.');
      setLoading(false);
      return;
    }

    const headers = headerWithAuth();
    if (!headers) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Event gifts are paginated through the V2 endpoint (same as the list page and
      // CreateEventGift), everything else (including stickers) uses the V1 endpoint.
      // V1 hides static backgrounds that have an `image` asset (old-client compat), so
      // those must be loaded through V2 like the list page does.
      const isStaticBackground =
        fetchCategoryParam === 'PROFILE' &&
        resolveProductTypeForCategory(fetchCategoryParam, typeParam || undefined) === 'background';
      const endpoint =
        fetchCategoryParam === 'EVENT_GIFT' || isStaticBackground
          ? `${walletApiBase}/getWalletShopProductsV2`
          : `${walletApiBase}/getWalletShopProducts`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          UserID: userId,
          CategoryKey: fetchCategoryParam,
          ...(fetchCategoryParam === 'PROFILE' || fetchCategoryParam === 'LIVE'
            ? { Type: resolveProductTypeForCategory(fetchCategoryParam, typeParam || undefined) }
            : {}),
          ...(fetchCategoryParam === 'EVENT_GIFT' ? { CurrencyType: 'token' } : {}),
          ...(isStaticBackground ? { CurrencyType: 'all' } : {}),
          IncludeInactive: true,
          IncludeAnimation: true,
          Environment: environmentParam || 'prod',
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.message || 'Failed to load products');
      }

      const products = (json.data?.products as WalletShopProduct[]) || [];
      const product = products.find((item) => item.ProductKey === productKeyParam);
      if (!product) {
        setMediaAssets([]);
        setFormState((prev) => ({
          ...prev,
          ProductKey: productKeyParam || prev.ProductKey,
          CategoryKey: fetchCategoryParam,
          Type: resolveProductTypeForCategory(fetchCategoryParam, typeParam || prev.Type),
          CurrencyType: fetchCategoryParam === 'LIVE' ? 'token' : prev.CurrencyType,
          Environment: environmentParam || prev.Environment,
        }));
        toast.info('Product not found. Fill the details to create it.');
        return;
      }

      const resolvedCategory: FetchCategoryKey = product.CategoryKey || fetchCategoryParam;
      const resolvedCurrencyType: 'coin' | 'token' = product.CurrencyType === 'token' ? 'token' : 'coin';
      setMediaAssets(product.MediaAssets || []);
      setOriginalPriceCoins(product.PriceCoins !== undefined ? product.PriceCoins : null);
      setOriginalPriceTokens(product.PriceTokens !== undefined ? product.PriceTokens : null);
      setFormState({
        ProductKey: product.ProductKey,
        Name: product.Name || '',
        Description: product.Description || '',
        CategoryKey: resolvedCategory,
        Type: resolveProductTypeForCategory(resolvedCategory, product.Type || typeParam || undefined),
        ThemePreference: product.ThemePreference || 'adaptive',
        Tags: (product.Tags || []).join(', '),
        AvailabilityType: product.AvailabilityType || availabilityTypes[0].value,
        AvailabilityStartAt: normalizeDateInput(product.AvailabilityStartAt),
        AvailabilityEndAt: normalizeDateInput(product.AvailabilityEndAt),
        QuantityAvailable: product.QuantityAvailable !== undefined ? String(product.QuantityAvailable) : '',
        QuantitySold: product.QuantitySold !== undefined ? String(product.QuantitySold) : '',
        PerUserLimit: product.PerUserLimit !== undefined ? String(product.PerUserLimit) : '',
        MaxPurchasePerOrder: product.MaxPurchasePerOrder !== undefined ? String(product.MaxPurchasePerOrder) : '',
        CurrencyType: resolvedCurrencyType,
        PriceCoins: product.PriceCoins !== undefined ? String(product.PriceCoins) : '',
        PriceTokens: product.PriceTokens !== undefined ? String(product.PriceTokens) : '',
        DiscountPriceCoins: product.DiscountPriceCoins !== undefined ? String(product.DiscountPriceCoins) : '',
        DiscountStartAt: normalizeDateInput(product.DiscountStartAt),
        DiscountEndAt: normalizeDateInput(product.DiscountEndAt),
        IsFeatured: !!product.IsFeatured,
        BadgeText: product.BadgeText || '',
        DisplayOrder: product.DisplayOrder !== undefined ? String(product.DisplayOrder) : '',
        Metadata: product.Metadata ? JSON.stringify(product.Metadata, null, 2) : '',
        IsActive: product.IsActive !== undefined ? !!product.IsActive : true,
        Environment: product.Environment || environmentParam || 'prod',
      });
    } catch (error) {
      toast.error((error as Error).message || 'Failed to fetch product');
    } finally {
      setLoading(false);
    }
  }, [fetchCategoryParam, environmentParam, headerWithAuth, productKeyParam, typeParam]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const handleSaveProduct = async () => {
    const headers = headerWithAuth();
    if (!headers) return;
    if (pendingMediaAssets.length) {
      toast.error('Upload pending assets before saving the product.');
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await fetch(`${walletApiBase}/updateWalletShopProduct`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.message || 'Failed to update product');
      }
      toast.success(json.message || 'Product updated');
      if (productKeyParam && payload.ProductKey && payload.ProductKey !== productKeyParam) {
        const nextParams = new URLSearchParams(searchParams.toString());
        nextParams.set('productKey', payload.ProductKey);
        nextParams.set('category', formState.CategoryKey);
        nextParams.set('type', formState.Type);
        nextParams.set('env', formState.Environment);
        navigate(`${window.location.pathname}?${nextParams.toString()}`, { replace: true });
      }
    } catch (error) {
      toast.error((error as Error).message || 'Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  const handleQueueAsset = async () => {
    const selectedMediaType = assetDraft.Type;
    const requiresDirectValue = directValueMediaTypes.has(selectedMediaType);
    const trimmedUrlValue = assetDraft.UrlValue.trim();

    if (!mediaRule.allowedTypes.includes(selectedMediaType)) {
      toast.error(`Media slot "${selectedMediaType}" is not valid for ${formState.Type}.`);
      return;
    }
    if (usedMediaTypeSet.has(selectedMediaType)) {
      toast.error(`${getMediaTypeLabel(selectedMediaType)} is already added. Remove it first to replace.`);
      return;
    }
    if (requiresDirectValue && !trimmedUrlValue) {
      toast.error('This media type needs a URL/value (for example a hex color).');
      return;
    }
    if (requiresDirectValue && trimmedUrlValue && !isHexColor(trimmedUrlValue)) {
      toast.error('Bubble media values must be a hex color like #F0DBE0.');
      return;
    }
    if (!requiresDirectValue && !draftFile && !trimmedUrlValue) {
      toast.error('Select a file or enter a direct URL/value.');
      return;
    }
    if (draftFile && trimmedUrlValue) {
      toast.error('Choose either a file or a direct URL/value, not both.');
      return;
    }
    if (requiresDirectValue && draftFile) {
      toast.error('Bubble media types should use a value like #F0DBE0.');
      return;
    }

    try {
      // Frame files: MIME, size and dimension limits checked before upload.
      const measured =
        draftFile && isProfileFrameType(formState.Type)
          ? await inspectProfileFrameFile(formState.Type, selectedMediaType, draftFile)
          : draftFile && isAnimatedBackgroundType(formState.Type)
            ? await inspectAnimatedBackgroundFile(selectedMediaType, draftFile)
            : {};
      const queuedAsset: PendingMediaAsset = {
        Id: `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
        Type: selectedMediaType,
        ...measured,
        File: draftFile || undefined,
        PreviewUrl: isImageFile(draftFile || undefined) ? URL.createObjectURL(draftFile as File) : undefined,
        UrlValue: trimmedUrlValue || undefined,
      };

      setPendingMediaAssets((prev) => [...prev, queuedAsset]);
      setDraftFile(null);
      setAssetDraft((prev) => ({
        ...prev,
        UrlValue: '',
      }));
      toast.success('Asset added to pending list');
    } catch (error) {
      toast.error((error as Error).message || 'Failed to queue asset');
    }
  };

  const handleUploadPendingAssets = async () => {
    const headers = headerWithAuth();
    if (!headers) return;

    if (!formState.ProductKey.trim()) {
      toast.error('ProductKey is required for asset upload.');
      return;
    }
    if (!pendingMediaAssets.length) {
      toast.error('Add assets to the pending list before upload.');
      return;
    }

    setUploading(true);
    setUploadResult(null);
    const failedIds = new Set<string>();
    const uploadedAssets: WalletShopMediaAsset[] = [];

    try {
      for (const pending of pendingMediaAssets) {
        try {
          let resolvedUrl = pending.UrlValue?.trim() || '';

          if (pending.File) {
            const presignedRes = await fetch(`${walletApiBase}/createWalletAssetUploadPresignedUrl`, {
              method: 'POST',
              headers,
              body: JSON.stringify({
                ProductKey: formState.ProductKey.trim().toUpperCase(),
                ContentType: pending.File.type || 'application/octet-stream',
                AssetType: pending.Type,
              }),
            });

            const presignedJson = await presignedRes.json();
            if (!presignedRes.ok) {
              throw new Error(presignedJson.message || 'Failed to generate upload URL');
            }

            const uploadData = presignedJson.data as UploadResult;
            const uploadBody = await pending.File.arrayBuffer();
            const uploadResponse = await fetch(uploadData.UploadUrl, {
              method: 'PUT',
              body: uploadBody,
            });

            if (!uploadResponse.ok) {
              const errorText = await uploadResponse.text();
              throw new Error(errorText || 'Failed to upload asset to storage');
            }

            setUploadResult(uploadData);
            resolvedUrl = uploadData.AssetUrl;
          }

          if (!resolvedUrl) {
            throw new Error('Missing URL for pending asset.');
          }

          uploadedAssets.push({
            Type: pending.Type,
            Url: resolvedUrl,
            MimeType: pending.File?.type || undefined,
            ...(pending.Width && pending.Height ? { Width: pending.Width, Height: pending.Height } : {}),
          });
        } catch (error) {
          failedIds.add(pending.Id);
          const reason = (error as Error).message || 'Unknown upload error';
          toast.error(`${pending.Type}: ${reason}`);
        }
      }

      if (uploadedAssets.length > 0) {
        upsertMediaAssets(uploadedAssets);
      }

      setPendingMediaAssets((prev) => {
        const remaining = prev.filter((pending) => failedIds.has(pending.Id));
        prev.forEach((pending) => {
          if (!failedIds.has(pending.Id) && pending.PreviewUrl) {
            URL.revokeObjectURL(pending.PreviewUrl);
          }
        });
        return remaining;
      });
      if (uploadedAssets.length > 0) {
        toast.success(`${uploadedAssets.length} asset(s) attached to MediaAssets`);
      }
      if (failedIds.size > 0) {
        toast.warn(`${failedIds.size} asset(s) failed and remain in the pending list.`);
      }
    } catch (error) {
      const message = (error as Error).message || 'Asset upload failed';
      const hint =
        message.toLowerCase().includes('failed to fetch') ? 'Upload failed (possible CORS or network issue).' : message;
      toast.error(hint);
    } finally {
      setUploading(false);
    }
  };

  const isDirectValueDraft = directValueMediaTypes.has(assetDraft.Type);
  const removePendingAsset = (id: string) => {
    setPendingMediaAssets((prev) => {
      const target = prev.find((item) => item.Id === id);
      if (target?.PreviewUrl) {
        URL.revokeObjectURL(target.PreviewUrl);
      }
      return prev.filter((item) => item.Id !== id);
    });
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Create / Update Wallet Shop Product</h1>
            <p className='text-sm text-gray-400'>Attach media assets first, then save product details.</p>
          </div>
          <button
            onClick={() => navigate(`/products?category=${formState.CategoryKey}&env=${formState.Environment}`)}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20'
          >
            Back to Assets
          </button>
        </div>

        {loading ? (
          <div className='text-sm text-gray-400'>Loading product...</div>
        ) : (
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-4'>
              <div>
                <p className='text-xs uppercase tracking-[0.2em] text-gray-500 mb-2'>Step 1</p>
                <h2 className='text-lg font-semibold text-white'>Prepare Asset Attachments</h2>
                <p className='text-xs text-gray-400 mt-1'>Queue multiple assets, then upload and attach in one step.</p>
              </div>
              <div className='space-y-3'>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>ProductKey</label>
                  <input
                    value={formState.ProductKey}
                    onChange={(e) => setFormState((prev) => ({ ...prev, ProductKey: e.target.value.toUpperCase() }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Product Type</label>
                  <select
                    value={formState.Type}
                    onChange={(e) => setFormState((prev) => ({ ...prev, Type: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  >
                    {allowedProductTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <p className='text-[11px] text-gray-500 mt-1'>
                    {formState.CategoryKey === 'GIFT'
                      ? 'GIFT products only allow Digital Gift type.'
                      : formState.CategoryKey === 'STICKER'
                      ? 'STICKER products only allow Sticker type.'
                      : formState.CategoryKey === 'EVENT_GIFT'
                      ? 'EVENT_GIFT products only allow Event Gift type.'
                      : 'PROFILE products support profile-specific types.'}
                  </p>
                </div>
                {isThemePreferenceSupported(formState.Type) && (
                  <div>
                    <label className='block text-xs text-gray-400 mb-1'>
                      Theme Preference <span className='text-amber-400'>*</span>
                    </label>
                    <select
                      value={formState.ThemePreference}
                      onChange={(e) =>
                        setFormState((prev) => ({
                          ...prev,
                          ThemePreference: e.target.value as 'adaptive' | 'light' | 'dark',
                        }))
                      }
                      className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                    >
                      <option value='adaptive' className='bg-[#1E1E2D]'>Adaptive (Matches app theme)</option>
                      <option value='light' className='bg-[#1E1E2D]'>Light (Always use light version)</option>
                      <option value='dark' className='bg-[#1E1E2D]'>Dark (Always use dark version)</option>
                    </select>
                    <p className='text-[11px] text-gray-400 mt-1'>
                      {formState.ThemePreference === 'adaptive' && 'Adaptive: Uses asset matching user app theme (falls back if only one mode is provided).'}
                      {formState.ThemePreference === 'light' && 'Light: Always uses light mode theme/background asset.'}
                      {formState.ThemePreference === 'dark' && 'Dark: Always uses dark mode theme/background asset.'}
                    </p>
                  </div>
                )}
                <div className='bg-black/20 border border-white/10 rounded-lg p-3 space-y-2'>
                  <p className='text-xs text-gray-300'>{mediaRule.helperText}</p>
                  {formState.Type === 'profile_frame' &&
                    usedMediaTypeSet.has('webm') &&
                    usedMediaTypeSet.has('ios_alpha') && (
                      <p className='text-xs text-amber-300'>
                        ⚠️ This is a <b>static</b> Profile Frame: the app shows the image, not the videos. To make it animate,
                        set Type to <b>Animated Profile Frame</b> and use a .webp thumbnail instead of the image.
                      </p>
                    )}
                  {dynamicRequiredTypes.length > 0 && (
                    <div className='flex flex-wrap gap-2'>
                      {dynamicRequiredTypes.map((type) => {
                        const isAttached = mediaAssets.some((asset) => asset.Type === type);
                        const isPending = pendingMediaAssets.some((asset) => asset.Type === type);
                        const statusClass = isAttached
                          ? 'border-emerald-400/50 text-emerald-300'
                          : isPending
                            ? 'border-amber-400/50 text-amber-300'
                            : 'border-white/20 text-gray-400';
                        const statusText = isAttached ? 'attached' : isPending ? 'pending' : 'missing';
                        return (
                          <span key={type} className={`text-[11px] px-2 py-1 rounded border ${statusClass}`}>
                            {getMediaTypeLabel(type)}: {statusText}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Media Slot</label>
                  <select
                    value={assetDraft.Type}
                    onChange={(e) => {
                      setDraftFile(null);
                      setAssetDraft((prev) => ({
                        ...prev,
                        Type: e.target.value,
                      }));
                    }}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  >
                    {mediaRule.allowedTypes.map((type) => (
                      <option key={type} value={type} disabled={usedMediaTypeSet.has(type)}>
                        {getMediaTypeLabel(type)}
                        {usedMediaTypeSet.has(type) ? ' (added)' : ''}
                      </option>
                    ))}
                  </select>
                  <p className='text-[11px] text-gray-500 mt-1'>Upload path key is auto-set from this media slot.</p>
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>
                    Asset URL / Value {isDirectValueDraft ? '(required for this media type)' : '(optional)'}
                  </label>
                  <input
                    value={assetDraft.UrlValue}
                    onChange={(e) => setAssetDraft((prev) => ({ ...prev, UrlValue: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                    placeholder={isDirectValueDraft ? '#F0DBE0' : 'https://cdn... or leave empty when uploading file'}
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>
                    File{jsonLikeMediaTypes.has(assetDraft.Type) ? ' (.json)' : (assetDraft.Type === 'webm' || assetDraft.Type === 'live_webm') ? ' (.webm)' : assetDraft.Type === 'audio' ? ' (audio)' : ''}
                  </label>
                  <input
                    type='file'
                    accept={jsonLikeMediaTypes.has(assetDraft.Type) ? 'application/json,.json' : (assetDraft.Type === 'webm' || assetDraft.Type === 'live_webm') ? 'video/webm,.webm' : assetDraft.Type === 'audio' ? 'audio/mpeg,audio/aac,audio/wav,audio/mp4,.mp3,.aac,.wav,.m4a' : undefined}
                    onChange={(e) => setDraftFile(e.target.files?.[0] || null)}
                    className='w-full text-sm text-gray-300'
                    disabled={isDirectValueDraft}
                  />
                </div>
                {draftFile && (
                  <div className='bg-black/20 border border-white/10 rounded-lg p-3 space-y-2'>
                    <div className='text-xs text-gray-300'>Selected: {draftFile.name}</div>
                    {draftPreviewUrl && (
                      <img src={draftPreviewUrl} alt='Selected asset preview' className='h-20 w-20 object-cover rounded border border-white/10' />
                    )}
                    {jsonLikeMediaTypes.has(assetDraft.Type) && (
                      <div className='text-xs text-amber-300'>JSON file — no preview available</div>
                    )}
                  </div>
                )}
                <button
                  onClick={handleQueueAsset}
                  disabled={!hasAvailableMediaSlots}
                  className='w-full py-2.5 bg-white/10 border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all'
                >
                  {hasAvailableMediaSlots ? 'Add To Pending List' : 'All Media Slots Are Added'}
                </button>
                <button
                  onClick={handleUploadPendingAssets}
                  disabled={uploading || !pendingMediaAssets.length}
                  className='w-full py-2.5 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-60'
                >
                  {uploading ? 'Uploading Pending Assets...' : `Upload & Attach (${pendingMediaAssets.length})`}
                </button>
              </div>

              {pendingMediaAssets.length > 0 && (
                <div className='bg-white/5 border border-white/10 rounded-lg p-3 space-y-2'>
                  <h3 className='text-sm font-semibold text-white'>Pending Attachments</h3>
                  {pendingMediaAssets.map((pending) => (
                    <div
                      key={pending.Id}
                      className='flex items-start justify-between gap-3 bg-black/20 rounded-lg px-3 py-2 text-xs text-gray-300'
                    >
                      <div className='min-w-0'>
                        <div className='text-gray-100'>{getMediaTypeLabel(pending.Type)}</div>
                        <div className='truncate'>
                          {pending.File ? pending.File.name : pending.UrlValue || '(no file or URL)'}
                        </div>
                        {pending.PreviewUrl && (
                          <img src={pending.PreviewUrl} alt={pending.Type} className='h-14 w-14 object-cover rounded border border-white/10 mt-2' />
                        )}
                      </div>
                      <button
                        onClick={() => removePendingAsset(pending.Id)}
                        className='text-red-300 hover:text-red-200'
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {uploadResult && (
                <div className='bg-white/5 border border-white/10 rounded-lg p-3 text-sm text-gray-300 space-y-2'>
                  <div>
                    Latest uploaded URL:{' '}
                    <a
                      href={uploadResult.AssetUrl}
                      target='_blank'
                      rel='noreferrer'
                      className='text-[#FF9C15] hover:underline'
                    >
                      {uploadResult.AssetUrl}
                    </a>
                  </div>
                  <div className='text-xs text-gray-500'>Key: {uploadResult.Key}</div>
                </div>
              )}
            </div>

            <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-4'>
              <div>
                <p className='text-xs uppercase tracking-[0.2em] text-gray-500 mb-2'>Step 2</p>
                <h2 className='text-lg font-semibold text-white'>Create / Update Product</h2>
                <p className='text-xs text-gray-400 mt-1'>Fill in product details and save together with attached assets.</p>
              </div>

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>ProductKey *</label>
                  <input
                    value={formState.ProductKey}
                    onChange={(e) => setFormState((prev) => ({ ...prev, ProductKey: e.target.value.toUpperCase() }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Name *</label>
                  <input
                    value={formState.Name}
                    onChange={(e) => setFormState((prev) => ({ ...prev, Name: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  />
                </div>
                <div className='sm:col-span-2'>
                  <label className='block text-xs text-gray-400 mb-1'>Description</label>
                  <textarea
                    value={formState.Description}
                    onChange={(e) => setFormState((prev) => ({ ...prev, Description: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                    rows={2}
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>CategoryKey *</label>
                  <select
                    value={formState.CategoryKey}
                    onChange={(e) => {
                      const nextCategory = e.target.value as FetchCategoryKey;
                      setFormState((prev) => ({
                        ...prev,
                        CategoryKey: nextCategory,
                        Type: resolveProductTypeForCategory(nextCategory, prev.Type),
                        CurrencyType: nextCategory === 'LIVE' ? 'token' : prev.CurrencyType,
                      }));
                    }}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  >
                    <option value='PROFILE'>PROFILE</option>
                    <option value='GIFT'>GIFT</option>
                    <option value='LIVE'>LIVE</option>
                    <option value='STICKER'>STICKER</option>
                    <option value='EVENT_GIFT'>EVENT_GIFT</option>
                  </select>
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Type * (product-level)</label>
                  <select
                    value={formState.Type}
                    onChange={(e) => setFormState((prev) => ({ ...prev, Type: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  >
                    {allowedProductTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                {isThemePreferenceSupported(formState.Type) && (
                  <div>
                    <label className='block text-xs text-gray-400 mb-1'>Theme Preference *</label>
                    <select
                      value={formState.ThemePreference}
                      onChange={(e) =>
                        setFormState((prev) => ({
                          ...prev,
                          ThemePreference: e.target.value as 'adaptive' | 'light' | 'dark',
                        }))
                      }
                      className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                    >
                      <option value='adaptive' className='bg-[#1E1E2D]'>Adaptive (Matches app theme)</option>
                      <option value='light' className='bg-[#1E1E2D]'>Light (Always use light version)</option>
                      <option value='dark' className='bg-[#1E1E2D]'>Dark (Always use dark version)</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Tags (comma separated)</label>
                  <input
                    value={formState.Tags}
                    onChange={(e) => setFormState((prev) => ({ ...prev, Tags: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Availability *</label>
                  <select
                    value={formState.AvailabilityType}
                    onChange={(e) => setFormState((prev) => ({ ...prev, AvailabilityType: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  >
                    {availabilityTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Currency Type *</label>
                  <div className='flex gap-2'>
                    {(['coin', 'token'] as const).map((ct) => (
                      <button
                        key={ct}
                        type='button'
                        onClick={() => setFormState((prev) => ({ ...prev, CurrencyType: ct }))}
                        className={`flex-1 py-2 text-xs font-semibold rounded-lg border transition ${
                          formState.CurrencyType === ct
                            ? ct === 'token'
                              ? 'bg-purple-500/30 border-purple-400/60 text-purple-200'
                              : 'bg-amber-500/30 border-amber-400/60 text-amber-200'
                            : 'bg-white/5 border-white/20 text-gray-400 hover:border-white/40'
                        }`}
                      >
                        {ct === 'coin' ? '🪙 Coin Gift' : '💎 Token Gift'}
                      </button>
                    ))}
                  </div>
                </div>
                {formState.CurrencyType === 'coin' ? (
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>
                    Price (Coins) *
                    {originalPriceCoins !== null && (
                      <span className='text-gray-500 font-normal'> — Current: {originalPriceCoins}</span>
                    )}
                  </label>
                  <input
                    value={formState.PriceCoins}
                    onChange={(e) => setFormState((prev) => ({ ...prev, PriceCoins: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                    type='number'
                    min='0'
                  />
                </div>
                ) : (
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>
                    Price (Tokens) *
                    {originalPriceTokens !== null && (
                      <span className='text-gray-500 font-normal'> — Current: {originalPriceTokens}</span>
                    )}
                  </label>
                  <input
                    value={formState.PriceTokens}
                    onChange={(e) => setFormState((prev) => ({ ...prev, PriceTokens: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-600'
                    type='number'
                    min='0'
                    placeholder='e.g. 50'
                  />
                </div>
                )}
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>DisplayOrder (optional)</label>
                  <input
                    value={formState.DisplayOrder}
                    onChange={(e) => setFormState((prev) => ({ ...prev, DisplayOrder: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                    type='number'
                    min='0'
                    placeholder='Leave blank to auto-assign on new product'
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Quantity Available</label>
                  <input
                    value={formState.QuantityAvailable}
                    onChange={(e) => setFormState((prev) => ({ ...prev, QuantityAvailable: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                    type='number'
                    min='0'
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Quantity Sold</label>
                  <input
                    value={formState.QuantitySold}
                    onChange={(e) => setFormState((prev) => ({ ...prev, QuantitySold: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                    type='number'
                    min='0'
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Per User Limit</label>
                  <input
                    value={formState.PerUserLimit}
                    onChange={(e) => setFormState((prev) => ({ ...prev, PerUserLimit: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                    type='number'
                    min='0'
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Max Purchase Per Order</label>
                  <input
                    value={formState.MaxPurchasePerOrder}
                    onChange={(e) => setFormState((prev) => ({ ...prev, MaxPurchasePerOrder: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                    type='number'
                    min='0'
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Discount Price</label>
                  <input
                    value={formState.DiscountPriceCoins}
                    onChange={(e) => setFormState((prev) => ({ ...prev, DiscountPriceCoins: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                    type='number'
                    min='0'
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Badge Text</label>
                  <input
                    value={formState.BadgeText}
                    onChange={(e) => setFormState((prev) => ({ ...prev, BadgeText: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Availability Start (ISO)</label>
                  <input
                    value={formState.AvailabilityStartAt}
                    onChange={(e) => setFormState((prev) => ({ ...prev, AvailabilityStartAt: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Availability End (ISO)</label>
                  <input
                    value={formState.AvailabilityEndAt}
                    onChange={(e) => setFormState((prev) => ({ ...prev, AvailabilityEndAt: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Discount Start (ISO)</label>
                  <input
                    value={formState.DiscountStartAt}
                    onChange={(e) => setFormState((prev) => ({ ...prev, DiscountStartAt: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Discount End (ISO)</label>
                  <input
                    value={formState.DiscountEndAt}
                    onChange={(e) => setFormState((prev) => ({ ...prev, DiscountEndAt: e.target.value }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  />
                </div>
                <div>
                  <label className='block text-xs text-gray-400 mb-1'>Environment</label>
                  <select
                    value={formState.Environment}
                    onChange={(e) => setFormState((prev) => ({ ...prev, Environment: e.target.value as 'dev' | 'prod' }))}
                    className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  >
                    <option value='prod'>prod</option>
                    <option value='dev'>dev</option>
                  </select>
                </div>
                <div className='flex items-center gap-2'>
                  <input
                    id='isFeatured'
                    type='checkbox'
                    checked={formState.IsFeatured}
                    onChange={(e) => setFormState((prev) => ({ ...prev, IsFeatured: e.target.checked }))}
                    className='h-4 w-4'
                  />
                  <label htmlFor='isFeatured' className='text-xs text-gray-300'>
                    Featured
                  </label>
                </div>
                <div className='flex items-center gap-2'>
                  <input
                    id='isActive'
                    type='checkbox'
                    checked={formState.IsActive}
                    onChange={(e) => setFormState((prev) => ({ ...prev, IsActive: e.target.checked }))}
                    className='h-4 w-4'
                  />
                  <label htmlFor='isActive' className='text-xs text-gray-300'>
                    Active
                  </label>
                </div>
              </div>

              <div>
                <label className='block text-xs text-gray-400 mb-1'>Metadata (JSON)</label>
                <textarea
                  value={formState.Metadata}
                  onChange={(e) => setFormState((prev) => ({ ...prev, Metadata: e.target.value }))}
                  className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white'
                  rows={4}
                />
              </div>

              {mediaAssets.length > 0 && (
                <div className='pt-2'>
                  <h3 className='text-sm font-semibold text-white mb-2'>Attached Assets</h3>
                  <div className='space-y-2'>
                    {mediaAssets.map((asset, index) => (
                      <div
                        key={`${asset.Url}-${index}`}
                      className='flex items-start justify-between gap-3 bg-white/5 rounded-lg px-3 py-2 text-sm text-gray-200'
                    >
                      <div className='min-w-0 space-y-1'>
                          <div className='text-xs text-gray-400'>{getMediaTypeLabel(asset.Type)}</div>
                          {isHexColor(asset.Url) ? (
                            <div className='flex items-center gap-2'>
                              <span
                                className='h-4 w-4 rounded border border-white/20'
                                style={{ backgroundColor: asset.Url }}
                              />
                              <span className='text-gray-200'>{asset.Url}</span>
                            </div>
                          ) : isLikelyHttpUrl(asset.Url) ? (
                            <a href={asset.Url} target='_blank' rel='noreferrer' className='text-[#FF9C15] hover:underline truncate block'>
                              {asset.Url}
                            </a>
                          ) : (
                            <div className='truncate'>{asset.Url}</div>
                          )}
                          {asset.ThumbnailUrl && isLikelyHttpUrl(asset.ThumbnailUrl) && (
                            <a
                              href={asset.ThumbnailUrl}
                              target='_blank'
                              rel='noreferrer'
                              className='text-xs text-sky-300 hover:underline truncate block'
                            >
                              Thumbnail: {asset.ThumbnailUrl}
                            </a>
                          )}
                          {isLikelyHttpUrl(asset.Url) &&
                            (isImageLikeAssetType(asset.Type) ||
                              asset.Type.includes('theme') ||
                              asset.Type.includes('thumbnail') ||
                              asset.MimeType?.startsWith('image/')) && (
                              <img
                                src={asset.Url}
                                alt={asset.Type}
                                className='h-16 w-16 object-cover rounded border border-white/10'
                              />
                            )}
                          {isLikelyHttpUrl(asset.Url) && ['webm', 'ios_alpha', 'live_webm', 'live_ios_alpha'].includes(asset.Type) && (
                            <video
                              src={asset.Url}
                              className='h-16 w-16 object-contain rounded border border-white/10 bg-[repeating-conic-gradient(#333_0_25%,#222_0_50%)] bg-[length:12px_12px]'
                              autoPlay
                              loop
                              muted
                              playsInline
                            />
                          )}
                        </div>
                        <button
                          onClick={() => setMediaAssets((prev) => prev.filter((_, idx) => idx !== index))}
                          className='text-red-300 hover:text-red-200'
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {missingRequiredMediaTypes.length > 0 && (
                <div className='bg-amber-500/10 border border-amber-400/40 rounded-lg px-3 py-2 text-xs text-amber-200'>
                  Missing required media slots: {missingRequiredMediaTypes.map(getMediaTypeLabel).join(', ')}
                </div>
              )}

              <button
                onClick={handleSaveProduct}
                disabled={saving}
                className='w-full py-2.5 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-60'
              >
                {saving ? 'Saving...' : 'Create / Update Product'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateWalletShopProduct;
